// What a browser without a session sees: signing in by email or with a
// passkey, or signing up, by email or by making a passkey, in today's
// welcome's look (welcome/Welcome.tsx there). Once either succeeds, the
// store loads the board, and the page the URL names shows. An email link
// shows "check your email" until it is opened (EmailSignIn.tsx); its own
// page, `/signin/:attempt`, finishes it here while signed out.
//
// Who may sign up is the server's setting: when sign-up is closed, the
// sign-up page says so instead of showing its form.
import { useEffect, useState, type FormEvent } from 'react'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import { noPasskeysText } from '../api/passkeys'
import { BoardLogo } from '../components/Icons'
import { useMounted } from '../components/layout'
import { useBoardState, useStore } from '../store/context'
import type { EmailOutcome, SignInOutcome } from '../store/store'
import { CheckEmail, EmailField, EmailLinkScreen, useEmailLink } from './EmailSignIn'
import { looksLikeAddress } from './emailLink'
import { emailWords } from './words'

/** What to tell the person when signing up or in failed; `doing` names it for anything else. */
function problemText(outcome: Extract<SignInOutcome, { ok: false }>, doing: string): string {
  switch (outcome.problem) {
    case 'refused':
      return 'The passkey was refused. Try again.'
    case 'cancelled':
      return 'The passkey prompt was cancelled.'
    case 'unsupported':
      return noPasskeysText()
    case 'failed':
      return `Could not ${doing}: ${outcome.message}`
  }
}

export function SignInPage() {
  const store = useStore()
  const { emailLink } = useBoardState()
  const navigate = useNavigate()
  const link = useMatch('/signin/:attempt')
  useEmailLink(link?.params.attempt ?? null)
  const [signingUp, setSigningUp] = useState(false)
  // What they typed, for "check your email" and for another try: in this page's memory and nowhere else.
  const [address, setAddress] = useState('')
  const [sent, setSent] = useState(false)
  let screen
  if (link || emailLink) {
    screen = (
      <EmailLinkScreen
        state={emailLink}
        onRestart={() => {
          store.clearEmailLink()
          setSent(false)
          if (link) navigate('/', { replace: true })
        }}
      />
    )
  } else if (sent) {
    screen = <CheckEmail address={address.trim()} onOther={() => setSent(false)} />
  } else {
    const form = { address, onAddress: setAddress, onSent: () => setSent(true) }
    screen = signingUp ? <SignUp {...form} onSignIn={() => setSigningUp(false)} /> : <SignIn {...form} onSignUp={() => setSigningUp(true)} />
  }
  return <div className="sign-in-page">{screen}</div>
}

interface EmailForm {
  address: string
  onAddress: (address: string) => void
  /** A link was asked for: "check your email". */
  onSent: () => void
}

/** Runs a ceremony, or asks for a link, and keeps what went wrong to show it. */
function useCeremony(doing: string) {
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const mounted = useMounted()
  const run = async (ceremony: () => Promise<SignInOutcome>) => {
    setBusy(true)
    setProblem(null)
    const outcome = await ceremony()
    // Once signed in, this page leaves.
    if (outcome.ok) return
    setProblem(problemText(outcome, doing))
    setBusy(false)
  }
  /** Asks for a link to `address`, once it reads as one. */
  const email = async (address: string, start: () => Promise<EmailOutcome>, sent: () => void) => {
    if (!looksLikeAddress(address)) {
      setProblem(emailWords.notAnAddress)
      return
    }
    setBusy(true)
    setProblem(null)
    const outcome = await start()
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) sent()
    else setProblem(outcome.message)
  }
  return { busy, problem, run, email, clear: () => setProblem(null) }
}

function Problem({ text }: { text: string | null }) {
  return text ? (
    <p className="sign-in-problem" role="alert">
      {text}
    </p>
  ) : null
}

/** The page's path, which signing in by a link comes back to. */
function useBack(): string {
  const { pathname, search } = useLocation()
  return pathname + search
}

function SignIn({ address, onAddress, onSent, onSignUp }: EmailForm & { onSignUp: () => void }) {
  const store = useStore()
  const back = useBack()
  const { busy, problem, run, email, clear } = useCeremony('sign in')
  const submit = (event: FormEvent) => {
    event.preventDefault()
    const typed = address.trim()
    if (!typed || busy) return
    void email(typed, () => store.startEmail(typed, null, back), onSent)
  }
  return (
    <section className="sign-in-card" aria-labelledby="sign-in-title">
      <BoardLogo size={48} />
      <div className="sign-in-head">
        <h1 id="sign-in-title">Sign in to Poulpe</h1>
      </div>
      <form className="sign-in-form" onSubmit={submit} noValidate>
        <EmailField value={address} onChange={(typed) => { onAddress(typed); clear() }} busy={busy} autoFocus />
        <Problem text={problem} />
        <button type="submit" className="button button-primary sign-in-button" disabled={busy || !address.trim()}>
          {emailWords.go}
        </button>
      </form>
      <button type="button" className="sign-in-quiet" disabled={busy} onClick={() => void run(() => store.signIn())}>
        Sign in with a passkey
      </button>
      <p className="sign-in-switch muted">
        New to Poulpe?{' '}
        <button type="button" className="link-button" disabled={busy} onClick={onSignUp}>
          Sign up
        </button>
      </p>
    </section>
  )
}

function SignedUpAlready({ busy, onSignIn }: { busy: boolean; onSignIn: () => void }) {
  return (
    <p className="sign-in-switch muted">
      Signed up already?{' '}
      <button type="button" className="link-button" disabled={busy} onClick={onSignIn}>
        Sign in
      </button>
    </p>
  )
}

function SignUp({ address, onAddress, onSent, onSignIn }: EmailForm & { onSignIn: () => void }) {
  const store = useStore()
  const back = useBack()
  const [name, setName] = useState('')
  const [workspace, setWorkspace] = useState('')
  const { busy, problem, run, email, clear } = useCeremony('sign up')
  // Whether sign-up is closed, or open by email alone: the form shows until the server says so.
  const [closed, setClosed] = useState(false)
  const [emailOnly, setEmailOnly] = useState(false)
  const mounted = useMounted()
  useEffect(() => {
    store.signUpStatus().then(
      (status) => {
        if (!mounted.current) return
        setClosed(status.closed && status.email_only !== true)
        setEmailOnly(status.email_only === true)
      },
      // The form stays: signing up says what went wrong.
      () => {},
    )
  }, [store, mounted])
  // A link signs up with both names, as a passkey does; the server checks them again.
  const ready = name.trim() !== '' && workspace.trim() !== '' && address.trim() !== ''
  const submit = (event: FormEvent) => {
    event.preventDefault()
    const typed = address.trim()
    if (!ready || busy) return
    void email(typed, () => store.startEmail(typed, { name: name.trim(), workspace: workspace.trim() }, back), onSent)
  }
  if (closed) {
    return (
      <section className="sign-in-card" aria-labelledby="sign-up-title">
        <BoardLogo size={48} />
        <div className="sign-in-head">
          <h1 id="sign-up-title">Sign up to Poulpe</h1>
          <p className="sign-in-line">Sign-up is closed on this server.</p>
        </div>
        <SignedUpAlready busy={busy} onSignIn={onSignIn} />
      </section>
    )
  }
  return (
    <section className="sign-in-card" aria-labelledby="sign-up-title">
      <BoardLogo size={48} />
      <div className="sign-in-head">
        <h1 id="sign-up-title">Sign up to Poulpe</h1>
      </div>
      <form className="sign-in-form" onSubmit={submit} noValidate>
        <label className="sign-in-field">
          <span>Your name</span>
          <input className="sign-in-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={100} required readOnly={busy} autoFocus />
        </label>
        <label className="sign-in-field">
          <span>Your first workspace</span>
          <input className="sign-in-input" value={workspace} onChange={(e) => setWorkspace(e.target.value)} autoComplete="organization" maxLength={100} required readOnly={busy} />
        </label>
        <EmailField value={address} onChange={(typed) => { onAddress(typed); clear() }} busy={busy} labelled />
        <Problem text={problem} />
        <button type="submit" className="button button-primary sign-in-button" disabled={busy || !ready}>
          {emailWords.go}
        </button>
      </form>
      {!emailOnly && (
        <button type="button" className="sign-in-quiet" disabled={busy} onClick={() => void run(() => store.signUp(name, workspace))}>
          Sign up with a passkey
        </button>
      )}
      <SignedUpAlready busy={busy} onSignIn={onSignIn} />
    </section>
  )
}
